import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppCommonModule } from '@app/components/app.common.module';
import { BaseComponent } from '@app/components/base/base.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { PlanPisoService } from '../plan-piso.service';
import { DealerService } from '@app/pages/dealers/services/dealer.service';

const PLAN_PISO_KEY_MXN_REGEX=  /^(MP|PL|77)\d{3}$/;
const PLAN_PISO_KEY_USD_REGEX=  /^(ML|77)\d{3}$/;
@Component({
  selector: 'app-new-plan-piso',
  standalone: true,
  imports: [AppCommonModule],
  templateUrl: './new-plan-piso.component.html',
  styleUrl: './new-plan-piso.component.css'
})
export class NewPlanPisoComponent extends BaseComponent implements OnInit {
  private readonly service = inject(PlanPisoService);
  private readonly dealerService = inject(DealerService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  isLoading: boolean = true;
  wholesaleForm: FormGroup;
  id: number | null = null;
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  activeOptions: any[] = [
    { label: 'Activo', value: 1 },
    { label: 'Inactivo', value: 0 }
  ];
  selectedActiveOption: any = null;
  dealers: any[] = [];
  dealerOptions: any[] = [];
  selectedDealer: any = null;
  selectedDealerId: number | null = null;
  dealerBranches: any[] = [];
  branchOptions: any[] = [];
  selectedBranchIds: number[] = [];
  dealerBranchNames: string = '';
  /** Referencia estable para "sin sucursales"; evita que getSelectedBranchValues() devuelva un [] nuevo en cada CD y dispare el efecto del multi-select en bucle. */
  private readonly emptyBranchIds: number[] = [];

  constructor(
    override route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService,
  ) {
    super(route, menu, notification);
    this.wholesaleForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      dealerId: [null as number | null],
      dealerBranchIds: [[]],
      wholeSaleKeyMxn: ['', [Validators.required, Validators.pattern(PLAN_PISO_KEY_MXN_REGEX)]],
      wholeSaleKeyUsd: ['', [Validators.pattern(PLAN_PISO_KEY_USD_REGEX)]],
      active: [1, [Validators.required]],
    });
    // Inicializar el valor por defecto del select
    this.selectedActiveOption = this.activeOptions[0];
  }

  ngOnInit(): void {
    this.sendDataToMenu();

    const url = this.router.url;
    this.isViewMode = url.includes('/view/');

    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('id');
      if (idParam) {
        this.id = Number(idParam);
        if (this.isViewMode) {
          this.wholesaleForm.disable();
        } else {
          this.isEditMode = true;
          this.wholesaleForm.enable();
        }
        this.loadDealers().then(() => {
          this.loadWholesale();
        }).catch(() => {});
      } else {
        this.wholesaleForm.enable();
        this.loadDealers().then(() => {
          this.isLoading = false;
        }).catch(() => {});
      }
    });
  }

  loadDealers(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.dealerService.getDealers().subscribe({
        next: (response) => {
          this.dealers = response.data || [];
          this.dealerOptions = this.dealers
            .map((dealer: any) => ({
              label: dealer.name,
              value: dealer.id
            }))
            .sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));
          resolve();
        },
        error: () => {
          this.isLoading = false;
          this.showNotificationError('Error al cargar los distribuidores.');
          reject();
        },
      });
    });
  }

  loadWholesale(): void {
    if (!this.id) {
      this.isLoading = false;
      return;
    }

    this.service.getWholesaleById(this.id).subscribe({
      next: (response) => {
        const wholesale = response.data;
        if (wholesale) {
          const activeValue = wholesale.active !== undefined ? wholesale.active : 1;
          this.selectedActiveOption = this.activeOptions.find(opt => opt.value === activeValue) || this.activeOptions[0];

          let dealerBranchIds = Array.isArray(wholesale.dealerBranchIds) ? wholesale.dealerBranchIds : [];
          let dealerBranchNames = Array.isArray(wholesale.dealerBranchNames)
            ? wholesale.dealerBranchNames.join(', ')
            : (wholesale.dealerBranchNames || '');

          if (dealerBranchIds.length === 0) {
            this.dealerService.getBranchesByWholesaleId(this.id!).subscribe({
              next: (branchRes) => {
                const branches = branchRes?.data || [];
                dealerBranchIds = branches
                  .filter((b: any) => b.dealerBranchId != null)
                  .map((b: any) => (typeof b.dealerBranchId === 'number' ? b.dealerBranchId : Number(b.dealerBranchId)));
                dealerBranchNames = branches
                  .filter((b: any) => b.dealerBranchName != null)
                  .map((b: any) => String(b.dealerBranchName))
                  .join(', ');
                const wholesaleToApply = { ...wholesale };
                if (!wholesaleToApply.dealerId && branches.length > 0 && branches[0].dealerId != null) {
                  wholesaleToApply.dealerId = branches[0].dealerId;
                }
                this.applyWholesaleData(wholesaleToApply, activeValue, dealerBranchIds, dealerBranchNames);
              },
              error: () => this.applyWholesaleData(wholesale, activeValue, [], ''),
            });
          } else {
            this.applyWholesaleData(wholesale, activeValue, dealerBranchIds, dealerBranchNames);
          }
          return;
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.showNotificationError('Error al cargar el plan piso.');
      },
    });
  }

  updateBranchWholesaleId(dealerId: number, branchId: number, wholesaleId: number | null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.dealerService.getBranchById(String(dealerId), String(branchId)).subscribe({
        next: (branchRes) => {
          const branch = branchRes.data;
          const payload = {
            id: branch.id,
            name: branch.name,
            address: branch.address || '',
            state: branch.state || '',
            postalCode: branch.postalCode || '',
            email: branch.email || '',
            phone: branch.phone || '',
            wholesaleId,
            sofomMarshId: branch.sofomMarshId,
            dtfsMarshId: branch.dtfsMarshId,
            enabled: branch.enabled ?? true,
            externalSalesforceId: branch.externalSalesforceId,
            externalProleasenetName: branch.externalProleasenetName,
          };
          this.dealerService.updateBranch(String(dealerId), String(branchId), payload).subscribe({
            next: () => resolve(),
            error: (err) => reject(err),
          });
        },
        error: (err) => reject(err),
      });
    });
  }

  onSubmit(): void {
    if (this.wholesaleForm.invalid) {
      this.showNotificationError('Por favor, completa todos los campos obligatorios correctamente.');
      return;
    }

    this.isLoading = true;
    const formData = this.wholesaleForm.getRawValue?.() ?? this.wholesaleForm.value;
    const dealerId = formData.dealerId;
    const dealerBranchIds: number[] = Array.isArray(formData.dealerBranchIds) ? formData.dealerBranchIds : [];

    const payload = {
      dealerId,
      wholeSaleKeyMxn: formData.wholeSaleKeyMxn,
      wholeSaleKeyUsd: formData.wholeSaleKeyUsd?.trim(),
      active: formData.active !== undefined ? formData.active : 1,
      name: formData.name
    };

    const assignBranchesToWholesale = (wholesaleId: number): Promise<void> => {
      const clearAndAssign = (branchesToClear: any[]) => {
        const promises: Promise<void>[] = [];
        branchesToClear.forEach((b: any) => {
          if (b.dealerId && b.dealerBranchId) {
            promises.push(this.updateBranchWholesaleId(b.dealerId, b.dealerBranchId, null));
          }
        });
        if (dealerId) {
          dealerBranchIds.forEach((branchId: number) => {
            promises.push(this.updateBranchWholesaleId(dealerId, branchId, wholesaleId));
          });
        }
        return promises.length === 0 ? Promise.resolve() : Promise.all(promises).then(() => {});
      };

      if (this.isEditMode && this.id) {
        return new Promise((resolve, reject) => {
          this.dealerService.getBranchesByWholesaleId(this.id!).subscribe({
            next: (res) => {
              const branchesToClear = (res.data || []).filter(
                (b: any) => !dealerBranchIds.includes(b.dealerBranchId)
              );
              clearAndAssign(branchesToClear).then(resolve).catch(reject);
            },
            error: reject,
          });
        });
      }
      return clearAndAssign([]);
    };

    if (this.isEditMode && this.id) {
      this.service.updateWholesale(this.id, payload).subscribe({
        next: (response) => {
          if (response.status) {
            assignBranchesToWholesale(this.id!).then(() => {
              this.isLoading = false;
              this.showNotificationSuccess('Plan piso actualizado exitosamente.');
              this.router.navigate(['catalogs/plan-piso/list']);
            }).catch((err) => {
              this.isLoading = false;
              this.showNotificationError('Plan piso actualizado, pero hubo un error al asignar la sucursal.');
              console.error('Error assigning branch:', err);
            });
          } else {
            this.isLoading = false;
            const errorMessage = response.errors?.[0] ?? 'Hubo un error al actualizar el plan piso.';
            this.showNotificationError(errorMessage);
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          const errorMessage = err?.error?.errors?.[0] ?? 'Hubo un error al actualizar el plan piso.';
          this.showNotificationError(errorMessage);
        },
      });
    } else {
      this.service.createWholesale(payload).subscribe({
        next: (response) => {
          if (response.status && response.data?.id) {
            const newWholesaleId = response.data.id;
            assignBranchesToWholesale(newWholesaleId).then(() => {
              this.isLoading = false;
              this.showNotificationSuccess('Plan piso creado exitosamente.');
              this.router.navigate(['catalogs/plan-piso/list']);
            }).catch((err) => {
              this.isLoading = false;
              this.showNotificationError('Plan piso creado, pero hubo un error al asignar la sucursal.');
              console.error('Error assigning branch:', err);
            });
          } else {
            this.isLoading = false;
            this.showNotificationSuccess('Plan piso creado exitosamente.');
            this.router.navigate(['catalogs/plan-piso/list']);
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          const errorMessage = err?.error?.errors?.[0] ?? 'Hubo un error al crear el plan piso.';
          this.showNotificationError(errorMessage);
        },
      });
    }
  }

  cancel(): void {
    this.router.navigate(['catalogs/plan-piso/list']);
  }

  goToEdit(): void {
    if (this.id) {
      this.router.navigate([`catalogs/plan-piso/edit/${this.id}`]);
    }
  }

  goBack(): void {
    this.router.navigate(['catalogs/plan-piso/list']);
  }

  onActiveChange(value: any): void {
    if (value !== null && value !== undefined) {
      // El componente app-select emite el valor directamente
      const activeValue = typeof value === 'object' ? value.value : value;
      this.wholesaleForm.patchValue({ active: activeValue });
      // Actualizar la opción seleccionada para mantener sincronizado
      this.selectedActiveOption = this.activeOptions.find(opt => opt.value === activeValue) || this.activeOptions[0];
    }
  }

  getSelectedActiveOption(): any {
    // Leer el valor actual del formulario
    const activeValue = this.wholesaleForm.get('active')?.value;
    // app-select busca por option.value === defaultValue, así que devolvemos el valor numérico
    return activeValue !== null && activeValue !== undefined ? activeValue : 1;
  }

  onDealerChange(value: any): void {
    const dealerId = (value !== null && value !== undefined)
      ? (typeof value === 'object' ? value.value : value)
      : null;
    this.wholesaleForm.patchValue({ dealerId, dealerBranchIds: [] });
    this.selectedDealer = dealerId != null ? this.dealerOptions.find(opt => opt.value === dealerId) ?? null : null;
    this.selectedDealerId = dealerId;
    this.selectedBranchIds = [];
    this.loadDealerBranches(dealerId);
  }

  onBranchesChange(selected: any[]): void {
    const ids = (selected || []).map((s: any) => s.id ?? s.value).filter((id: any) => id != null);
    this.wholesaleForm.patchValue({ dealerBranchIds: ids });
    this.selectedBranchIds = ids;
  }

  private applyWholesaleData(
    wholesale: any,
    activeValue: number,
    dealerBranchIds: number[],
    dealerBranchNamesStr: string
  ): void {
    this.wholesaleForm.patchValue({
      name: wholesale.name || '',
      dealerId: wholesale.dealerId ?? null,
      dealerBranchIds,
      wholeSaleKeyMxn: wholesale.wholeSaleKeyMxn || '',
      wholeSaleKeyUsd: wholesale.wholeSaleKeyUsd || '',
      active: activeValue,
    });
    this.dealerBranchNames = dealerBranchNamesStr;
    this.selectedBranchIds = dealerBranchIds;
    if (wholesale.dealerId && this.dealerOptions.length > 0) {
      this.selectedDealer = this.dealerOptions.find(opt => opt.value === wholesale.dealerId);
      this.selectedDealerId = wholesale.dealerId;
      this.loadDealerBranches(wholesale.dealerId).then(() => {
        this.isLoading = false;
      });
    } else {
      this.isLoading = false;
    }
  }

  loadDealerBranches(dealerId: number | null): Promise<void> {
    if (!dealerId) {
      this.dealerBranches = [];
      this.branchOptions = [];
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.dealerService.getDealerById(String(dealerId)).subscribe({
        next: (response) => {
          const dealer = response.data;
          this.dealerBranches = (dealer?.dealerBranches || []).filter((b: any) => b.enabled !== false);
          this.branchOptions = this.dealerBranches.map((b: any) => ({
            id: b.id,
            name: b.name,
            value: b.id,
            description: b.name
          }));
          resolve();
        },
        error: () => {
          this.dealerBranches = [];
          this.branchOptions = [];
          resolve();
        },
      });
    });
  }

  getSelectedBranchValues(): any[] {
    const ids = this.selectedBranchIds?.length ? this.selectedBranchIds : this.wholesaleForm.get('dealerBranchIds')?.value;
    return (Array.isArray(ids) && ids.length > 0) ? ids : this.emptyBranchIds;
  }

  getSelectedDealerOption(): any {
    // Usar selectedDealerId si está disponible, sino usar el valor del formulario
    const dealerId = this.selectedDealerId !== null ? this.selectedDealerId : this.wholesaleForm.get('dealerId')?.value;
    // app-select busca por option.value === defaultValue, así que devolvemos el valor numérico
    return dealerId !== null && dealerId !== undefined ? dealerId : null;
  }

  getDealerName(dealerId: number): string {
    const dealer = this.dealers.find((d: any) => d.id === dealerId);
    return dealer ? dealer.name : dealerId?.toString() || '-';
  }

  getSubTitle(): string {
    if (this.isViewMode || this.isEditMode) {
      const name = this.wholesaleForm.get('name')?.value;
      return name ? `Plan Piso ${name}` : 'Plan Piso';
    } else {
      return 'Nueva plan piso';
    }
  }
}

